import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Divider } from '../components/common';
import toast from 'react-hot-toast';
import { useMediaQuery } from '../hooks';

const AnimatedAnalyticsHero = () => (
  <div style={{ position: 'relative', width: '100%', maxWidth: 500, padding: 20, fontFamily: 'var(--font-sans)', zIndex: 20 }}>
    <h1 className="hero-anim-up-1" style={{ fontSize: '2.5rem', fontWeight: 700, color: '#fff', lineHeight: 1.1, marginBottom: 16 }}>
      Smarter Email Insights <br/><span style={{ color: 'var(--cyan)' }}>for Modern Teams.</span>
    </h1>
    <p className="hero-anim-up-1" style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 44, maxWidth: 380, lineHeight: 1.7 }}>
      Track opens, analyze clicks, and act on live interaction data directly from your personal dashboard.
    </p>

    <div className="hero-anim-float" style={{ position: 'relative' }}>
      {/* Mock Dashboard Element */}
      <div style={{ 
        background: 'rgba(11,22,48,0.7)', border: '1px solid var(--border-accent)', 
        borderRadius: 16, padding: 24, backdropFilter: 'blur(10px)',
        boxShadow: '0 20px 40px rgba(0,0,0,0.4), 0 0 40px rgba(0, 225, 200, 0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Campaign Open Rate</span>
          <span style={{ fontSize: '0.75rem', background: 'var(--cyan-dim)', color: 'var(--cyan)', padding: '2px 8px', borderRadius: 12 }}>● LIVE</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <span style={{ fontSize: '3.2rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>68.4<span style={{ fontSize: '1.8rem', color: 'var(--text-secondary)' }}>%</span></span>
          <span style={{ fontSize: '0.9rem', color: 'var(--green)', fontWeight: 600, marginBottom: 6 }}>↑ 12.4%</span>
        </div>
        
        {/* Animated Line Chart SVG */}
        <svg style={{ width: '100%', height: 50, marginTop: 16, overflow: 'visible' }} viewBox="0 0 200 40">
          <polyline className="hero-line-draw" points="0,35 30,28 60,20 90,10 120,4 150,-2 180,-6 200,-8" 
            fill="none" stroke="var(--cyan)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          <polygon className="hero-anim-up-2" points="0,35 30,28 60,20 90,10 120,4 150,-2 180,-6 200,-8 200,40 0,40" 
            fill="rgba(0,225,200,0.15)" stroke="none" />
        </svg>
      </div>

      {/* Floating Card 1 */}
      <div className="hero-anim-bob-1 hero-anim-up-2" style={{
        position: 'absolute', top: -30, right: -30, width: 220,
        background: 'rgba(11,22,48,0.9)', border: '1px solid rgba(245,166,35,0.3)',
        borderRadius: 12, padding: '14px 18px', boxShadow: '0 8px 30px rgba(0,0,0,0.5)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--amber-dim)', color: 'var(--amber)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🔗</div>
          <div>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff' }}>Link Clicked</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Pricing Page</div>
          </div>
        </div>
      </div>

      {/* Floating Card 2 */}
      <div className="hero-anim-bob-2 hero-anim-up-3" style={{
        position: 'absolute', bottom: -24, left: -24, width: 200,
        background: 'rgba(11,22,48,0.9)', border: '1px solid rgba(129,140,248,0.3)',
        borderRadius: 12, padding: '14px 18px', boxShadow: '0 8px 30px rgba(0,0,0,0.5)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgb(129,140,248)', boxShadow: '0 0 10px rgb(129,140,248)' }} />
          <div>
            <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#fff' }}>Email Forwarded</div>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>To 2 recipients</div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AuthShell = ({ children, title, subtitle }) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  return (
  <div style={{
    minHeight: '100vh',
    height: isMobile ? '100vh' : 'auto',
    background: 'var(--bg-base)',
    display: 'flex',
    alignItems: isMobile ? 'flex-start' : 'center',
    justifyContent: 'center',
    padding: isMobile ? 16 : 40,
    position: 'relative',
    overflow: 'hidden',
  }}>
    {/* Animated Background */}
    <div className="bg-grid"></div>
    <div className="orb" style={{ width: 500, height: 500, background: 'rgba(0, 225, 200, 0.08)', top: -50, left: -100, filter: 'blur(100px)' }}></div>
    <div className="orb" style={{ width: 400, height: 400, background: 'rgba(245, 166, 35, 0.06)', bottom: -80, right: -50, filter: 'blur(90px)' }}></div>
    <div className="bg-vignette"></div>

    <div style={{ 
      display: 'flex', 
      width: '100%', 
      maxWidth: 1000, 
      gap: 60,
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 20
    }}>
      
      {/* Animated Hero: Side on Desktop, Background on Mobile */}
      <div style={{ 
        flex: isMobile ? 'none' : 1, 
        display: 'flex', 
        justifyContent: isMobile ? 'center' : 'flex-start', 
        alignItems: isMobile ? 'flex-start' : 'center',
        position: isMobile ? 'absolute' : 'relative',
        inset: isMobile ? 0 : 'auto',
        paddingTop: isMobile ? 80 : 0, 
        pointerEvents: isMobile ? 'none' : 'auto',
        zIndex: isMobile ? 5 : 'auto',
        opacity: isMobile ? 0.45 : 1,
        transform: isMobile ? 'scale(0.85)' : 'none',
        transformOrigin: 'top center',
      }}>
        <AnimatedAnalyticsHero />
      </div>

      {/* Right Side: Auth Card Wrapper to handle margins */}
      <div style={{ width: '100%', maxWidth: 420, display: 'flex', justifyContent: 'center' }}>
        <div style={{
          width: '100%',
          marginTop: isMobile ? 24 : 0,
          background: isMobile ? 'rgba(17,17,24,0.65)' : 'var(--bg-surface)',
          backdropFilter: isMobile ? 'blur(16px)' : 'none',
          WebkitBackdropFilter: isMobile ? 'blur(16px)' : 'none',
          border: '1px solid rgba(255,255,255,0.05)',
          borderRadius: 'var(--radius-xl)',
          padding: isMobile ? '28px 24px' : '40px 36px',
          animation: 'fadeIn 0.3s ease',
          position: 'relative',
          zIndex: 10,
          boxShadow: isMobile ? '0 10px 40px rgba(0,0,0,0.5)' : 'none',
        }}>
      {/* Logo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 36, justifyContent: 'center' }}>
        <img 
          src="/logo.png" 
          alt="EmailTracker" 
          style={{
            height: isMobile ? 36 : 48, 
            width: 'auto',
            maxWidth: '100%',
            objectFit: 'contain',
            borderRadius: 'var(--radius-sm)'
          }} 
          onError={(e) => {
            // Fallback CSS logo if image isn't placed yet
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
            e.target.parentElement.style.justifyContent = 'flex-start'; // Revert back to original flex-start if fallback triggers
          }}
        />
        <div style={{
          display: 'none', // shown on img error
          width: 36, height: 36, borderRadius: 10,
          background: 'var(--cyan)', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.9rem', fontWeight: 700, color: '#0a0a0f', fontFamily: 'var(--font-mono)',
          boxShadow: 'var(--cyan-glow)',
        }}>ET</div>
        <span style={{ display: 'none' /* Assuming the larger logo contains the required brand text */ }}>EmailTracker</span>
      </div>

        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.7rem', marginBottom: 6 }}>{title}</h2>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: 28 }}>{subtitle}</p>
        {children}
      </div>
      </div>
    </div>
  </div>
);
};

export const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    const errs = {};
    if (!form.email) errs.email = 'Required';
    if (!form.password) errs.password = 'Required';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your tracking dashboard">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Input label="Email" type="email" value={form.email} onChange={set('email')} error={errors.email} placeholder="you@example.com" autoFocus />
        <Input label="Password" type="password" value={form.password} onChange={set('password')} error={errors.password} placeholder="••••••••" />
        <Button type="submit" loading={loading} size="lg" style={{ marginTop: 8, fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>
          Sign In
        </Button>
        <Divider label="or" />
        <p style={{ textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
          No account?{' '}
          <Link to="/register" style={{ color: 'var(--cyan)', fontWeight: 600 }}>Create one →</Link>
        </p>
      </form>
    </AuthShell>
  );
};

export const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    const errs = {};
    if (!form.name || form.name.length < 2) errs.name = 'At least 2 characters';
    if (!form.email) errs.email = 'Required';
    if (!form.password || form.password.length < 8) errs.password = 'Min 8 characters';
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      toast.success('Account created!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create account" subtitle="Start tracking your emails for free">
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <Input label="Full Name" value={form.name} onChange={set('name')} error={errors.name} placeholder="Alice Smith" autoFocus />
        <Input label="Email" type="email" value={form.email} onChange={set('email')} error={errors.email} placeholder="you@example.com" />
        <Input label="Password" type="password" value={form.password} onChange={set('password')} error={errors.password} placeholder="Min 8 characters" />
        <Button type="submit" loading={loading} size="lg" style={{ marginTop: 8, fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }}>
          Create Account
        </Button>
        <Divider label="or" />
        <p style={{ textAlign: 'center', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
          Already registered?{' '}
          <Link to="/login" style={{ color: 'var(--cyan)', fontWeight: 600 }}>Sign in →</Link>
        </p>
      </form>
    </AuthShell>
  );
};
